import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    AlertCircle,
    Bell,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Code,
    Copy,
    Edit,
    Eye,
    FileText,
    Filter,
    Info,
    Languages,
    Mail,
    MessageSquare,
    Palette,
    Plus,
    Search,
    Settings,
    Trash2,
    Zap,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useConfirm } from '@/hooks/useConfirm';
import {
    type NotificationTemplate,
    useCreateNotificationTemplate,
    useDeleteNotificationTemplate,
    useTestNotificationTemplate,
    useUpdateNotificationTemplate,
} from '@/api/notifications/use-notifications.tsx';
import { GENERAL_VARIABLES } from '@/data/notification-trigger-variables';

// Rich Text Editor Component - Now using TipTap

interface TemplateManagerProps {
    templates: NotificationTemplate[];
    isLoading: boolean;
    onRefresh: () => void;
}

export const TemplateManager: React.FC<TemplateManagerProps> = ({
    templates = [],
    isLoading,
    onRefresh,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const confirm = useConfirm();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<string>('all');

    const createMutation = useCreateNotificationTemplate();
    const updateMutation = useUpdateNotificationTemplate();
    const deleteMutation = useDeleteNotificationTemplate();
    const testMutation = useTestNotificationTemplate();

    // Filter and paginate templates
    const filteredTemplates = templates.filter(template => {
        const matchesSearch =
            template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            template.description?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesType = filterType === 'all' || template.type === filterType;
        const matchesStatus =
            filterStatus === 'all' ||
            (filterStatus === 'active' && template.is_active) ||
            (filterStatus === 'inactive' && !template.is_active);

        return matchesSearch && matchesType && matchesStatus;
    });

    const totalPages = Math.ceil(filteredTemplates.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentTemplates = filteredTemplates.slice(startIndex, endIndex);

    const handleCreateTemplate = async (data: any) => {
        try {
            await createMutation.mutateAsync(data);
            enqueueSnackbar('Template created successfully', { variant: 'success' });
            setIsCreateDialogOpen(false);
            onRefresh();
        } catch (error) {
            enqueueSnackbar('Failed to create template', { variant: 'error' });
        }
    };

    const handleUpdateTemplate = async (id: string, data: any) => {
        try {
            await updateMutation.mutateAsync({ id, data });
            enqueueSnackbar('Template updated successfully', { variant: 'success' });
            setIsEditDialogOpen(false);
            setSelectedTemplate(null);
            onRefresh();
        } catch (error) {
            enqueueSnackbar('Failed to update template', { variant: 'error' });
        }
    };

    const handleDeleteTemplate = async (template: NotificationTemplate) => {
        const ok = await confirm({
            title: 'Delete template?',
            description: `"${template.name}" will be permanently removed.`,
            confirmLabel: 'Delete',
            variant: 'destructive',
        });
        if (!ok) return;
        try {
            await deleteMutation.mutateAsync(template.id);
            enqueueSnackbar('Template deleted successfully', { variant: 'success' });
            onRefresh();
        } catch (error) {
            enqueueSnackbar('Failed to delete template', { variant: 'error' });
        }
    };

    const handleTestTemplate = async (id: string) => {
        try {
            await testMutation.mutateAsync({ id, data: { test_data: 'test' } });
            enqueueSnackbar('Test notification sent successfully', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to send test notification', { variant: 'error' });
        }
    };

    const handleDuplicateTemplate = (template: NotificationTemplate) => {
        setSelectedTemplate({
            ...template,
            id: '',
            name: `${template.name} (Copy)`,
        });
        setIsCreateDialogOpen(true);
    };

    const getChannelIcon = (channel: string) => {
        switch (channel) {
            case 'email':
                return <Mail className="h-4 w-4" />;
            case 'sms':
                return <MessageSquare className="h-4 w-4" />;
            case 'push':
                return <Bell className="h-4 w-4" />;
            case 'in_app':
                return <Eye className="h-4 w-4" />;
            default:
                return <Settings className="h-4 w-4" />;
        }
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'automatic':
                return <Zap className="h-4 w-4" />;
            case 'manual':
                return <Eye className="h-4 w-4" />;
            case 'custom':
                return <Edit className="h-4 w-4" />;
            default:
                return <FileText className="h-4 w-4" />;
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <FileText className="h-8 w-8 mx-auto mb-4 text-muted-foreground animate-pulse" />
                    <p className="text-muted-foreground">Loading templates...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border bg-card">
                <div>
                    <h2 className="text-2xl font-bold">
                        Notification Templates ({filteredTemplates.length})
                    </h2>
                    <p className="text-muted-foreground">
                        Manage notification templates for different channels and languages
                    </p>
                </div>
                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                    <DialogTrigger asChild>
                        <Button>
                            <Plus className="h-4 w-4" />
                            Create Template
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Create New Template</DialogTitle>
                        </DialogHeader>
                        <CreateTemplateForm onSubmit={handleCreateTemplate} />
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filters and Search */}
            <div className="p-6 border-b border-border bg-card">
                <div className="flex items-center gap-4 flex-wrap">
                    {/* Search */}
                    <div className="flex items-center gap-2 flex-1 min-w-[300px]">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search templates..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="max-w-sm"
                        />
                    </div>

                    {/* Type Filter */}
                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-muted-foreground" />
                        <Select value={filterType} onValueChange={setFilterType}>
                            <SelectTrigger className="w-[140px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Types</SelectItem>
                                <SelectItem value="automatic">Automatic</SelectItem>
                                <SelectItem value="manual">Manual</SelectItem>
                                <SelectItem value="custom">Custom</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Status Filter */}
                    <div className="flex items-center gap-2">
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger className="w-[140px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="inactive">Inactive</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Items per page */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Show:</span>
                        <Select
                            value={itemsPerPage.toString()}
                            onValueChange={value => setItemsPerPage(Number(value))}
                        >
                            <SelectTrigger className="w-[80px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="5">5</SelectItem>
                                <SelectItem value="10">10</SelectItem>
                                <SelectItem value="20">20</SelectItem>
                                <SelectItem value="50">50</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Templates List */}
            <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-4">
                    {currentTemplates.map(template => (
                        <Card
                            key={template.id}
                            className="w-full hover:shadow-md transition-shadow"
                        >
                            <CardHeader className="pb-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {getTypeIcon(template.type)}
                                        <div>
                                            <CardTitle className="text-lg">
                                                {template.name}
                                            </CardTitle>
                                            <p className="text-sm text-muted-foreground capitalize">
                                                {template.type}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDuplicateTemplate(template)}
                                            aria-label={`Duplicate ${template.name}`}
                                            title="Duplicate"
                                        >
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleTestTemplate(template.id)}
                                            disabled={testMutation.isPending}
                                            aria-label={`Test ${template.name}`}
                                            title="Test Template"
                                        >
                                            <Zap className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setSelectedTemplate(template);
                                                setIsEditDialogOpen(true);
                                            }}
                                            aria-label={`Edit ${template.name}`}
                                            title="Edit"
                                        >
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteTemplate(template)}
                                            disabled={deleteMutation.isPending}
                                            aria-label={`Delete ${template.name}`}
                                            title="Delete"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    {/* Description */}
                                    <div className="lg:col-span-2">
                                        <h4 className="font-medium mb-2">Description</h4>
                                        <div className="text-sm text-muted-foreground">
                                            {template.description ? (
                                                <div
                                                    className="prose prose-sm max-w-none"
                                                    dangerouslySetInnerHTML={{
                                                        __html: template.description,
                                                    }}
                                                />
                                            ) : (
                                                <p className="italic">No description provided</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Metadata */}
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-medium mb-2">Template Info</h4>
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className="capitalize">
                                                        {template.type}
                                                    </Badge>
                                                    {template.is_active ? (
                                                        <Badge
                                                            variant="default"
                                                            className="bg-success text-success-foreground"
                                                        >
                                                            Active
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="secondary">Inactive</Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="font-medium mb-2">Channels</h4>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {template.channels.map(channel => (
                                                    <span
                                                        key={channel}
                                                        className="text-sm"
                                                        title={channel}
                                                    >
                                                        {getChannelIcon(channel)}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="font-medium mb-2">Languages</h4>
                                            <div className="flex items-center gap-2">
                                                <Languages className="h-4 w-4 text-muted-foreground" />
                                                <span className="text-sm text-muted-foreground">
                                                    {template.supported_languages.length} languages
                                                </span>
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="font-medium mb-2">Usage</h4>
                                            <div className="text-sm text-muted-foreground">
                                                Used {template.usage_count} times
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {currentTemplates.length === 0 && (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <FileText className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
                            <h3 className="text-lg font-medium mb-2">No templates found</h3>
                            <p className="text-muted-foreground mb-4">
                                {searchQuery || filterType !== 'all' || filterStatus !== 'all'
                                    ? 'Try adjusting your search or filters'
                                    : 'Create your first notification template'}
                            </p>
                            {!searchQuery && filterType === 'all' && filterStatus === 'all' && (
                                <Button onClick={() => setIsCreateDialogOpen(true)}>
                                    <Plus className="h-4 w-4" />
                                    Create Template
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between p-6 border-t border-border bg-card">
                    <div className="text-sm text-muted-foreground">
                        Showing {startIndex + 1} to {Math.min(endIndex, filteredTemplates.length)}{' '}
                        of {filteredTemplates.length} templates
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                        >
                            <ChevronLeft className="h-4 w-4" />
                            Previous
                        </Button>
                        <div className="flex items-center gap-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <Button
                                    key={page}
                                    variant={page === currentPage ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setCurrentPage(page)}
                                    className="w-8 h-8 p-0"
                                >
                                    {page}
                                </Button>
                            ))}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                        >
                            Next
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Edit Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit Template</DialogTitle>
                    </DialogHeader>
                    {selectedTemplate && (
                        <EditTemplateForm
                            template={selectedTemplate}
                            onSubmit={data => handleUpdateTemplate(selectedTemplate.id, data)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Create Template Form Component
const CreateTemplateForm: React.FC<{ onSubmit: (data: any) => void }> = ({ onSubmit }) => {
    const [formData, setFormData] = useState({
        name: '',
        type: 'automatic',
        description: '',
        is_active: true,
        contents: [
            {
                language: 'en',
                channel: 'email',
                subject: '',
                html_content: '',
            },
        ],
    });

    const [activeLanguage, setActiveLanguage] = useState('en');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [previewMode, setPreviewMode] = useState<Record<string, boolean>>({});

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        const validationErrors: Record<string, string> = {};

        // Validate basic fields
        if (!formData.name.trim()) {
            validationErrors.name = 'Name is required';
        }

        // Validate content for each language-channel combination (html_content only)
        formData.contents.forEach(content => {
            const key = `${content.language}_${content.channel}`;

            if (content.channel === 'email') {
                if (!content.subject?.trim()) {
                    validationErrors[`${key}_subject`] = 'Email subject is required';
                }
                if (!content.html_content?.trim()) {
                    validationErrors[`${key}_content`] = 'Email content is required';
                }
            } else {
                if (!content.html_content?.trim()) {
                    validationErrors[`${key}_content`] =
                        `${content.channel.toUpperCase()} content is required`;
                }
            }
        });

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        // Filter out contents without html_content
        const validContents = formData.contents
            .filter(content => {
                if (content.channel === 'email') {
                    return content.subject?.trim() && content.html_content?.trim();
                }
                return content.html_content?.trim();
            })
            .map(content => ({
                language: content.language,
                channel: content.channel,
                subject: content.subject,
                html_content: content.html_content,
                channel_specific_data:
                    (content as { channel_specific_data?: unknown[] }).channel_specific_data ?? [],
            }));

        // Calculate supported channels and languages from valid content only
        const supportedLanguages = [...new Set(validContents.map(c => c.language))];
        const supportedChannels = [...new Set(validContents.map(c => c.channel))];
        const defaultLanguage = supportedLanguages[0] || 'en';

        const submitData = {
            ...formData,
            contents: validContents,
        };

        onSubmit(submitData);
    };

    const addLanguage = (language: string) => {
        // Add the new language with email channel by default
        setFormData(prev => ({
            ...prev,
            contents: [
                ...prev.contents,
                {
                    language,
                    channel: 'email',
                    subject: '',
                    html_content: '',
                },
            ],
        }));
        setActiveLanguage(language);
        // Clear errors for new content
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`${language}_email_subject`];
            delete newErrors[`${language}_email_content`];
            return newErrors;
        });
    };

    const removeLanguage = (language: string) => {
        const newContents = formData.contents.filter(c => c.language !== language);
        setFormData(prev => ({ ...prev, contents: newContents }));

        // Switch to first available language if removing current
        if (activeLanguage === language) {
            const remainingLanguages = [...new Set(newContents.map(c => c.language))];
            setActiveLanguage(remainingLanguages[0] || 'en');
        }

        // Clear errors for removed language
        setErrors(prev => {
            const newErrors = { ...prev };
            Object.keys(newErrors).forEach(key => {
                if (key.startsWith(`${language}_`)) {
                    delete newErrors[key];
                }
            });
            return newErrors;
        });
    };

    const addChannelToLanguage = (language: string, channel: string) => {
        setFormData(prev => ({
            ...prev,
            contents: [
                ...prev.contents,
                {
                    language,
                    channel,
                    subject: '',
                    html_content: '',
                },
            ],
        }));
        // Clear errors for new content
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`${language}_${channel}_subject`];
            delete newErrors[`${language}_${channel}_content`];
            return newErrors;
        });
    };

    const removeContent = (language: string, channel: string) => {
        setFormData(prev => ({
            ...prev,
            contents: prev.contents.filter(
                c => !(c.language === language && c.channel === channel)
            ),
        }));

        // Clear errors for removed content
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`${language}_${channel}_subject`];
            delete newErrors[`${language}_${channel}_content`];
            return newErrors;
        });
    };

    const updateContent = (language: string, channel: string, field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            contents: prev.contents.map(content =>
                content.language === language && content.channel === channel
                    ? { ...content, [field]: value }
                    : content
            ),
        }));

        // Clear error when user starts typing
        const errorKey = `${language}_${channel}_${field}`;
        if (errors[errorKey]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[errorKey];
                return newErrors;
            });
        }
    };

    // Get available languages to add
    const getAvailableLanguages = () => {
        const existingLanguages = new Set(formData.contents.map(c => c.language));
        const allLanguages = [
            { code: 'en', name: 'English' },
            { code: 'ar', name: 'Arabic' },
            { code: 'es', name: 'Spanish' },
            { code: 'fr', name: 'French' },
        ];
        return allLanguages.filter(lang => !existingLanguages.has(lang.code));
    };

    // Get available channels for a specific language
    const getAvailableChannelsForLanguage = (language: string) => {
        const existingChannels = new Set(
            formData.contents.filter(c => c.language === language).map(c => c.channel)
        );
        const allChannels = ['email', 'sms', 'push', 'in_app'];
        return allChannels.filter(channel => !existingChannels.has(channel));
    };

    // Get content for a specific language
    const getContentForLanguage = (language: string) => {
        return formData.contents.filter(c => c.language === language);
    };

    const availableLanguages = getAvailableLanguages();
    const hasEnglishEmail = formData.contents.some(
        c => c.language === 'en' && c.channel === 'email'
    );
    const languages = [...new Set(formData.contents.map(c => c.language))].sort();

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    <TabsTrigger value="content">Content by Language</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-sm font-medium">Name</Label>
                            <Input
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Template name"
                                required
                                className={errors.name ? 'border-destructive' : ''}
                            />
                            {errors.name && (
                                <p className="text-sm text-destructive mt-1">{errors.name}</p>
                            )}
                        </div>
                        <div>
                            <Label className="text-sm font-medium">Type</Label>
                            <Select
                                value={formData.type}
                                onValueChange={value => setFormData({ ...formData, type: value })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="automatic">Automatic</SelectItem>
                                    <SelectItem value="manual">Manual</SelectItem>
                                    <SelectItem value="custom">Custom</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label className="text-sm font-medium">Description</Label>
                        <Textarea
                            value={formData.description}
                            onChange={e =>
                                setFormData({ ...formData, description: e.target.value })
                            }
                            placeholder="Template description"
                            className="min-h-[80px] mt-1"
                        />
                    </div>

                    <div className="flex items-center space-x-2">
                        <Switch
                            checked={formData.is_active}
                            onCheckedChange={checked =>
                                setFormData({ ...formData, is_active: checked })
                            }
                        />
                        <Label className="text-sm font-medium">Active</Label>
                    </div>
                </TabsContent>

                <TabsContent value="content" className="space-y-6">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-medium">Content by Language</h3>
                                <p className="text-sm text-muted-foreground">
                                    English email content is required. Add additional languages and
                                    channels as needed.
                                </p>
                            </div>

                            {availableLanguages.length > 0 && (
                                <div className="flex items-center gap-2">
                                    <Select onValueChange={value => addLanguage(value)}>
                                        <SelectTrigger className="w-[150px]">
                                            <SelectValue placeholder="Add language..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableLanguages.map(lang => (
                                                <SelectItem key={lang.code} value={lang.code}>
                                                    {lang.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>

                        {/* Available Variables Reference */}
                        <Card className="bg-info/10 border-info/20 text-foreground">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Info className="h-4 w-4" />
                                    Available Variables for All Templates
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div>
                                    <p className="text-xs font-semibold text-muted-foreground mb-2">
                                        General Variables (auto-injected in all templates):
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {GENERAL_VARIABLES.map(variable => (
                                            <Badge
                                                key={variable.name}
                                                variant="secondary"
                                                className="text-xs font-mono cursor-help"
                                                title={variable.description}
                                            >
                                                {`{{${variable.name}}}`}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground italic">
                                    💡 Tip: Hover over a variable to see its description. When you
                                    create an automation rule with a specific trigger, additional
                                    trigger-specific variables will be available. Use variables in
                                    your template with the{' '}
                                    <code className="bg-muted px-1 rounded">{`{{variable_name}}`}</code>{' '}
                                    syntax.
                                </p>
                            </CardContent>
                        </Card>

                        {/* Language Tabs */}
                        {languages.length > 0 && (
                            <Tabs
                                value={activeLanguage}
                                onValueChange={setActiveLanguage}
                                className="w-full"
                            >
                                <TabsList
                                    className="grid w-full"
                                    style={{
                                        gridTemplateColumns: `repeat(${languages.length}, 1fr)`,
                                    }}
                                >
                                    {languages.map(lang => (
                                        <TabsTrigger
                                            key={lang}
                                            value={lang}
                                            className="flex items-center gap-2"
                                        >
                                            {lang === 'en'
                                                ? '🇺🇸'
                                                : lang === 'ar'
                                                  ? '🇸🇦'
                                                  : lang === 'es'
                                                    ? '🇪🇸'
                                                    : '🇫🇷'}
                                            {lang === 'en'
                                                ? 'English'
                                                : lang === 'ar'
                                                  ? 'Arabic'
                                                  : lang === 'es'
                                                    ? 'Spanish'
                                                    : 'French'}
                                            {lang === 'en' && (
                                                <Badge
                                                    variant="default"
                                                    className="bg-info text-info-foreground text-xs"
                                                >
                                                    Required
                                                </Badge>
                                            )}
                                            {languages.length > 1 && lang !== 'en' && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-4 w-4 p-0 ms-1"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        removeLanguage(lang);
                                                    }}
                                                    aria-label={`Remove ${lang} language`}
                                                    title={`Remove ${lang} language`}
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                </Button>
                                            )}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>

                                {languages.map(lang => (
                                    <TabsContent key={lang} value={lang} className="space-y-4">
                                        <div className="space-y-4">
                                            {/* Content for each channel in this language */}
                                            {getContentForLanguage(lang).map((content, index) => (
                                                <div
                                                    key={`${lang}-${content.channel}`}
                                                    className="border rounded-lg p-4 space-y-4"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <Badge variant="outline">
                                                                {content.channel.toUpperCase()}
                                                            </Badge>
                                                            {lang === 'en' &&
                                                                content.channel === 'email' && (
                                                                    <Badge
                                                                        variant="default"
                                                                        className="bg-info text-info-foreground"
                                                                    >
                                                                        Required
                                                                    </Badge>
                                                                )}
                                                        </div>
                                                        {!(
                                                            lang === 'en' &&
                                                            content.channel === 'email'
                                                        ) && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() =>
                                                                    removeContent(
                                                                        lang,
                                                                        content.channel
                                                                    )
                                                                }
                                                                aria-label={`Remove ${content.channel} content for ${lang}`}
                                                                title={`Remove ${content.channel} content`}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    </div>

                                                    {content.channel === 'email' && (
                                                        <div>
                                                            <Label className="text-sm font-medium">
                                                                Subject
                                                            </Label>
                                                            <Input
                                                                value={content.subject}
                                                                onChange={e =>
                                                                    updateContent(
                                                                        lang,
                                                                        content.channel,
                                                                        'subject',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                placeholder="Email subject"
                                                                required={lang === 'en'}
                                                                className={
                                                                    errors[
                                                                        `${lang}_${content.channel}_subject`
                                                                    ]
                                                                        ? 'border-destructive'
                                                                        : ''
                                                                }
                                                            />
                                                            {errors[
                                                                `${lang}_${content.channel}_subject`
                                                            ] && (
                                                                <p className="text-sm text-destructive mt-1">
                                                                    {
                                                                        errors[
                                                                            `${lang}_${content.channel}_subject`
                                                                        ]
                                                                    }
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}

                                                    <div>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <Label className="text-sm font-medium">
                                                                {content.channel
                                                                    .charAt(0)
                                                                    .toUpperCase() +
                                                                    content.channel.slice(1)}{' '}
                                                                Content
                                                            </Label>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    const key = `${lang}_${content.channel}`;
                                                                    setPreviewMode(prev => ({
                                                                        ...prev,
                                                                        [key]: !prev[key],
                                                                    }));
                                                                }}
                                                            >
                                                                {previewMode[
                                                                    `${lang}_${content.channel}`
                                                                ] ? (
                                                                    <>
                                                                        <Code className="h-4 w-4" />
                                                                        Edit
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Eye className="h-4 w-4" />
                                                                        Preview
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </div>
                                                        {previewMode[
                                                            `${lang}_${content.channel}`
                                                        ] ? (
                                                            <div className="border rounded-md p-4 min-h-[200px] bg-card">
                                                                <div
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: content.html_content,
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <Textarea
                                                                value={content.html_content}
                                                                onChange={e =>
                                                                    updateContent(
                                                                        lang,
                                                                        content.channel,
                                                                        'html_content',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                placeholder={`${content.channel} content for ${
                                                                    lang === 'en'
                                                                        ? 'English'
                                                                        : lang === 'ar'
                                                                          ? 'Arabic'
                                                                          : lang === 'es'
                                                                            ? 'Spanish'
                                                                            : 'French'
                                                                }`}
                                                                className="min-h-[200px] font-mono text-sm"
                                                            />
                                                        )}
                                                        {errors[
                                                            `${lang}_${content.channel}_content`
                                                        ] && (
                                                            <p className="text-sm text-destructive mt-1">
                                                                {
                                                                    errors[
                                                                        `${lang}_${content.channel}_content`
                                                                    ]
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Add Channel Button - Moved to bottom */}
                                            {getAvailableChannelsForLanguage(lang).length > 0 && (
                                                <div className="flex justify-center pt-4 border-t">
                                                    <Select
                                                        onValueChange={channel =>
                                                            addChannelToLanguage(lang, channel)
                                                        }
                                                    >
                                                        <SelectTrigger className="w-[200px]">
                                                            <SelectValue placeholder="Add channel..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {getAvailableChannelsForLanguage(
                                                                lang
                                                            ).map(channel => (
                                                                <SelectItem
                                                                    key={channel}
                                                                    value={channel}
                                                                >
                                                                    {channel.toUpperCase()}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            )}
                                        </div>
                                    </TabsContent>
                                ))}
                            </Tabs>
                        )}

                        {/* Summary */}
                        <div className="bg-muted p-4 rounded-lg">
                            <h4 className="font-medium mb-2">Template Summary</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-muted-foreground">
                                        Supported Languages:{' '}
                                    </span>
                                    <span className="font-medium">
                                        {languages
                                            .map(lang =>
                                                lang === 'en'
                                                    ? 'English'
                                                    : lang === 'ar'
                                                      ? 'Arabic'
                                                      : lang === 'es'
                                                        ? 'Spanish'
                                                        : 'French'
                                            )
                                            .join(', ')}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">
                                        Supported Channels:{' '}
                                    </span>
                                    <span className="font-medium">
                                        {[...new Set(formData.contents.map(c => c.channel))]
                                            .map(channel => channel.toUpperCase())
                                            .join(', ')}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {!hasEnglishEmail && (
                            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                                <div className="flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 text-warning" />
                                    <span className="text-sm text-warning">
                                        English email content is required for all templates.
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline">
                    Cancel
                </Button>
                <Button type="submit" disabled={!hasEnglishEmail}>
                    Create Template
                </Button>
            </div>
        </form>
    );
};

// Edit Template Form Component
const EditTemplateForm: React.FC<{
    template: NotificationTemplate;
    onSubmit: (data: any) => void;
}> = ({ template, onSubmit }) => {
    // Convert content_variations to contents array format (html_content only)
    const convertContentVariationsToContents = (variations: Record<string, any> | null) => {
        if (!variations) {
            return [
                {
                    language: 'en',
                    channel: 'email',
                    subject: '',
                    html_content: '',
                },
            ];
        }

        const contents: Array<{
            language: string;
            channel: string;
            subject: string;
            html_content: string;
        }> = [];

        Object.entries(variations).forEach(([key, content]) => {
            // Split only on the first underscore to handle channel names with underscores like "in_app"
            const firstUnderscoreIndex = key.indexOf('_');
            const language = key.substring(0, firstUnderscoreIndex);
            const channel = key.substring(firstUnderscoreIndex + 1);

            contents.push({
                language,
                channel,
                subject: content.subject || '',
                html_content: content.html_content || content.content || '',
            });
        });

        return contents;
    };

    const [formData, setFormData] = useState({
        name: template.name,
        type: template.type,
        description: template.description || '',
        is_active: template.is_active,
        contents: convertContentVariationsToContents(template.content_variations),
    });

    const [activeLanguage, setActiveLanguage] = useState('en');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [previewMode, setPreviewMode] = useState<Record<string, boolean>>({});

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        const validationErrors: Record<string, string> = {};

        // Validate basic fields
        if (!formData.name.trim()) {
            validationErrors.name = 'Name is required';
        }

        // Validate content for each language-channel combination (html_content only)
        formData.contents.forEach(content => {
            const key = `${content.language}_${content.channel}`;

            if (content.channel === 'email') {
                if (!content.subject?.trim()) {
                    validationErrors[`${key}_subject`] = 'Email subject is required';
                }
                if (!content.html_content?.trim()) {
                    validationErrors[`${key}_content`] = 'Email content is required';
                }
            } else {
                if (!content.html_content?.trim()) {
                    validationErrors[`${key}_content`] =
                        `${content.channel.toUpperCase()} content is required`;
                }
            }
        });

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        // Filter out contents without html_content
        const validContents = formData.contents
            .filter(content => {
                if (content.channel === 'email') {
                    return content.subject?.trim() && content.html_content?.trim();
                }
                return content.html_content?.trim();
            })
            .map(content => ({
                language: content.language,
                channel: content.channel,
                subject: content.subject,
                html_content: content.html_content,
                channel_specific_data:
                    (content as { channel_specific_data?: unknown[] }).channel_specific_data ?? [],
            }));

        // Calculate supported channels and languages from valid content only
        const supportedLanguages = [...new Set(validContents.map(c => c.language))];
        const supportedChannels = [...new Set(validContents.map(c => c.channel))];
        const defaultLanguage = supportedLanguages[0] || 'en';

        const submitData = {
            ...formData,
            contents: validContents,
        };

        onSubmit(submitData);
    };

    const addLanguage = (language: string) => {
        // Add the new language with email channel by default
        setFormData(prev => ({
            ...prev,
            contents: [
                ...prev.contents,
                {
                    language,
                    channel: 'email',
                    subject: '',
                    html_content: '',
                },
            ],
        }));
        setActiveLanguage(language);
        // Clear errors for new content
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`${language}_email_subject`];
            delete newErrors[`${language}_email_content`];
            return newErrors;
        });
    };

    const removeLanguage = (language: string) => {
        const newContents = formData.contents.filter(c => c.language !== language);
        setFormData(prev => ({ ...prev, contents: newContents }));

        // Switch to first available language if removing current
        if (activeLanguage === language) {
            const remainingLanguages = [...new Set(newContents.map(c => c.language))];
            setActiveLanguage(remainingLanguages[0] || 'en');
        }

        // Clear errors for removed language
        setErrors(prev => {
            const newErrors = { ...prev };
            Object.keys(newErrors).forEach(key => {
                if (key.startsWith(`${language}_`)) {
                    delete newErrors[key];
                }
            });
            return newErrors;
        });
    };

    const addChannelToLanguage = (language: string, channel: string) => {
        setFormData(prev => ({
            ...prev,
            contents: [
                ...prev.contents,
                {
                    language,
                    channel,
                    subject: '',
                    html_content: '',
                },
            ],
        }));
        // Clear errors for new content
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`${language}_${channel}_subject`];
            delete newErrors[`${language}_${channel}_content`];
            return newErrors;
        });
    };

    const removeContent = (language: string, channel: string) => {
        setFormData(prev => ({
            ...prev,
            contents: prev.contents.filter(
                c => !(c.language === language && c.channel === channel)
            ),
        }));

        // Clear errors for removed content
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`${language}_${channel}_subject`];
            delete newErrors[`${language}_${channel}_content`];
            return newErrors;
        });
    };

    const updateContent = (language: string, channel: string, field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            contents: prev.contents.map(content =>
                content.language === language && content.channel === channel
                    ? { ...content, [field]: value }
                    : content
            ),
        }));

        // Clear error when user starts typing
        const errorKey = `${language}_${channel}_${field}`;
        if (errors[errorKey]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[errorKey];
                return newErrors;
            });
        }
    };

    // Get available languages to add
    const getAvailableLanguages = () => {
        const existingLanguages = new Set(formData.contents.map(c => c.language));
        const allLanguages = [
            { code: 'en', name: 'English' },
            { code: 'ar', name: 'Arabic' },
            { code: 'es', name: 'Spanish' },
            { code: 'fr', name: 'French' },
        ];
        return allLanguages.filter(lang => !existingLanguages.has(lang.code));
    };

    // Get available channels for a specific language
    const getAvailableChannelsForLanguage = (language: string) => {
        const existingChannels = new Set(
            formData.contents.filter(c => c.language === language).map(c => c.channel)
        );
        const allChannels = ['email', 'sms', 'push', 'in_app'];
        return allChannels.filter(channel => !existingChannels.has(channel));
    };

    // Get content for a specific language
    const getContentForLanguage = (language: string) => {
        return formData.contents.filter(c => c.language === language);
    };

    const availableLanguages = getAvailableLanguages();
    const hasEnglishEmail = formData.contents.some(
        c => c.language === 'en' && c.channel === 'email'
    );
    const languages = [...new Set(formData.contents.map(c => c.language))].sort();

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    <TabsTrigger value="content">Content by Language</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-sm font-medium">Name</Label>
                            <Input
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Template name"
                                required
                                className={errors.name ? 'border-destructive' : ''}
                            />
                            {errors.name && (
                                <p className="text-sm text-destructive mt-1">{errors.name}</p>
                            )}
                        </div>
                        <div>
                            <Label className="text-sm font-medium">Type</Label>
                            <Select
                                value={formData.type}
                                onValueChange={value => setFormData({ ...formData, type: value })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="automatic">Automatic</SelectItem>
                                    <SelectItem value="manual">Manual</SelectItem>
                                    <SelectItem value="custom">Custom</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label className="text-sm font-medium">Description</Label>
                        <Textarea
                            value={formData.description}
                            onChange={e =>
                                setFormData({ ...formData, description: e.target.value })
                            }
                            placeholder="Template description"
                            className="min-h-[80px] mt-1"
                        />
                    </div>

                    <div className="flex items-center space-x-2">
                        <Switch
                            checked={formData.is_active}
                            onCheckedChange={checked =>
                                setFormData({ ...formData, is_active: checked })
                            }
                        />
                        <Label className="text-sm font-medium">Active</Label>
                    </div>
                </TabsContent>

                <TabsContent value="content" className="space-y-6">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-medium">Content by Language</h3>
                                <p className="text-sm text-muted-foreground">
                                    English email content is required. Add additional languages and
                                    channels as needed.
                                </p>
                            </div>

                            {availableLanguages.length > 0 && (
                                <div className="flex items-center gap-2">
                                    <Select onValueChange={value => addLanguage(value)}>
                                        <SelectTrigger className="w-[150px]">
                                            <SelectValue placeholder="Add language..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableLanguages.map(lang => (
                                                <SelectItem key={lang.code} value={lang.code}>
                                                    {lang.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>

                        {/* Available Variables Reference */}
                        <Card className="bg-info/10 border-info/20 text-foreground">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Info className="h-4 w-4" />
                                    Available Variables for All Templates
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div>
                                    <p className="text-xs font-semibold text-muted-foreground mb-2">
                                        General Variables (auto-injected in all templates):
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {GENERAL_VARIABLES.map(variable => (
                                            <Badge
                                                key={variable.name}
                                                variant="secondary"
                                                className="text-xs font-mono cursor-help"
                                                title={variable.description}
                                            >
                                                {`{{${variable.name}}}`}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground italic">
                                    💡 Tip: Hover over a variable to see its description. When you
                                    create an automation rule with a specific trigger, additional
                                    trigger-specific variables will be available. Use variables in
                                    your template with the{' '}
                                    <code className="bg-muted px-1 rounded">{`{{variable_name}}`}</code>{' '}
                                    syntax.
                                </p>
                            </CardContent>
                        </Card>

                        {/* Language Tabs */}
                        {languages.length > 0 && (
                            <Tabs
                                value={activeLanguage}
                                onValueChange={setActiveLanguage}
                                className="w-full"
                            >
                                <TabsList
                                    className="grid w-full"
                                    style={{
                                        gridTemplateColumns: `repeat(${languages.length}, 1fr)`,
                                    }}
                                >
                                    {languages.map(lang => (
                                        <TabsTrigger
                                            key={lang}
                                            value={lang}
                                            className="flex items-center gap-2"
                                        >
                                            {lang === 'en'
                                                ? '🇺🇸'
                                                : lang === 'ar'
                                                  ? '🇸🇦'
                                                  : lang === 'es'
                                                    ? '🇪🇸'
                                                    : '🇫🇷'}
                                            {lang === 'en'
                                                ? 'English'
                                                : lang === 'ar'
                                                  ? 'Arabic'
                                                  : lang === 'es'
                                                    ? 'Spanish'
                                                    : 'French'}
                                            {lang === 'en' && (
                                                <Badge
                                                    variant="default"
                                                    className="bg-info text-info-foreground text-xs"
                                                >
                                                    Required
                                                </Badge>
                                            )}
                                            {languages.length > 1 && lang !== 'en' && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-4 w-4 p-0 ms-1"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        removeLanguage(lang);
                                                    }}
                                                    aria-label={`Remove ${lang} language`}
                                                    title={`Remove ${lang} language`}
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                </Button>
                                            )}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>

                                {languages.map(lang => (
                                    <TabsContent key={lang} value={lang} className="space-y-4">
                                        <div className="space-y-4">
                                            {/* Content for each channel in this language */}
                                            {getContentForLanguage(lang).map((content, index) => (
                                                <div
                                                    key={`${lang}-${content.channel}`}
                                                    className="border rounded-lg p-4 space-y-4"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <Badge variant="outline">
                                                                {content.channel.toUpperCase()}
                                                            </Badge>
                                                            {lang === 'en' &&
                                                                content.channel === 'email' && (
                                                                    <Badge
                                                                        variant="default"
                                                                        className="bg-info text-info-foreground"
                                                                    >
                                                                        Required
                                                                    </Badge>
                                                                )}
                                                        </div>
                                                        {!(
                                                            lang === 'en' &&
                                                            content.channel === 'email'
                                                        ) && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() =>
                                                                    removeContent(
                                                                        lang,
                                                                        content.channel
                                                                    )
                                                                }
                                                                aria-label={`Remove ${content.channel} content for ${lang}`}
                                                                title={`Remove ${content.channel} content`}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    </div>

                                                    {content.channel === 'email' && (
                                                        <div>
                                                            <Label className="text-sm font-medium">
                                                                Subject
                                                            </Label>
                                                            <Input
                                                                value={content.subject}
                                                                onChange={e =>
                                                                    updateContent(
                                                                        lang,
                                                                        content.channel,
                                                                        'subject',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                placeholder="Email subject"
                                                                required={lang === 'en'}
                                                                className={
                                                                    errors[
                                                                        `${lang}_${content.channel}_subject`
                                                                    ]
                                                                        ? 'border-destructive'
                                                                        : ''
                                                                }
                                                            />
                                                            {errors[
                                                                `${lang}_${content.channel}_subject`
                                                            ] && (
                                                                <p className="text-sm text-destructive mt-1">
                                                                    {
                                                                        errors[
                                                                            `${lang}_${content.channel}_subject`
                                                                        ]
                                                                    }
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}

                                                    <div>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <Label className="text-sm font-medium">
                                                                {content.channel
                                                                    .charAt(0)
                                                                    .toUpperCase() +
                                                                    content.channel.slice(1)}{' '}
                                                                Content
                                                            </Label>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    const key = `${lang}_${content.channel}`;
                                                                    setPreviewMode(prev => ({
                                                                        ...prev,
                                                                        [key]: !prev[key],
                                                                    }));
                                                                }}
                                                            >
                                                                {previewMode[
                                                                    `${lang}_${content.channel}`
                                                                ] ? (
                                                                    <>
                                                                        <Code className="h-4 w-4" />
                                                                        Edit
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Eye className="h-4 w-4" />
                                                                        Preview
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </div>
                                                        {previewMode[
                                                            `${lang}_${content.channel}`
                                                        ] ? (
                                                            <div className="border rounded-md p-4 min-h-[200px] bg-card">
                                                                <div
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: content.html_content,
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <Textarea
                                                                value={content.html_content}
                                                                onChange={e =>
                                                                    updateContent(
                                                                        lang,
                                                                        content.channel,
                                                                        'html_content',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                placeholder={`${content.channel} content for ${
                                                                    lang === 'en'
                                                                        ? 'English'
                                                                        : lang === 'ar'
                                                                          ? 'Arabic'
                                                                          : lang === 'es'
                                                                            ? 'Spanish'
                                                                            : 'French'
                                                                }`}
                                                                className="min-h-[200px] font-mono text-sm"
                                                            />
                                                        )}
                                                        {errors[
                                                            `${lang}_${content.channel}_content`
                                                        ] && (
                                                            <p className="text-sm text-destructive mt-1">
                                                                {
                                                                    errors[
                                                                        `${lang}_${content.channel}_content`
                                                                    ]
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Add Channel Button - Moved to bottom */}
                                            {getAvailableChannelsForLanguage(lang).length > 0 && (
                                                <div className="flex justify-center pt-4 border-t">
                                                    <Select
                                                        onValueChange={channel =>
                                                            addChannelToLanguage(lang, channel)
                                                        }
                                                    >
                                                        <SelectTrigger className="w-[200px]">
                                                            <SelectValue placeholder="Add channel..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {getAvailableChannelsForLanguage(
                                                                lang
                                                            ).map(channel => (
                                                                <SelectItem
                                                                    key={channel}
                                                                    value={channel}
                                                                >
                                                                    {channel.toUpperCase()}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            )}
                                        </div>
                                    </TabsContent>
                                ))}
                            </Tabs>
                        )}

                        {/* Summary */}
                        <div className="bg-muted p-4 rounded-lg">
                            <h4 className="font-medium mb-2">Template Summary</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-muted-foreground">
                                        Supported Languages:{' '}
                                    </span>
                                    <span className="font-medium">
                                        {languages
                                            .map(lang =>
                                                lang === 'en'
                                                    ? 'English'
                                                    : lang === 'ar'
                                                      ? 'Arabic'
                                                      : lang === 'es'
                                                        ? 'Spanish'
                                                        : 'French'
                                            )
                                            .join(', ')}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">
                                        Supported Channels:{' '}
                                    </span>
                                    <span className="font-medium">
                                        {[...new Set(formData.contents.map(c => c.channel))]
                                            .map(channel => channel.toUpperCase())
                                            .join(', ')}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {!hasEnglishEmail && (
                            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                                <div className="flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 text-warning" />
                                    <span className="text-sm text-warning">
                                        English email content is required for all templates.
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline">
                    Cancel
                </Button>
                <Button type="submit" disabled={!hasEnglishEmail}>
                    Update Template
                </Button>
            </div>
        </form>
    );
};
